import React, { useState, useEffect } from 'react';
import {X} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { lumi } from '../lib/lumi';
import toast from 'react-hot-toast';
import { getReferralSource } from '../lib/referralTracking';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BookingModal: React.FC<BookingModalProps> = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isReferred, setIsReferred] = useState<string>('');
  const [travelAgency, setTravelAgency] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Get referral source on component mount
  useEffect(() => {
    const referralSource = getReferralSource();
    if (referralSource && !travelAgency) {
      setTravelAgency(referralSource);
      setIsReferred('yes');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !message) return;

    setSubmitting(true);
    
    try {
      // Save contact form data to database
      // Get referral source
      const referralSource = getReferralSource();
      
      await lumi.entities.contacts.create({
        name,
        email,
        travelAgency: travelAgency || referralSource || undefined,
        message,
        createdAt: new Date().toISOString(),
        creator: 'contact_form'
      });
      
      toast.success('Contact information saved successfully!');
      
      // Redirect to VoyageVax booking page
      window.location.href = 'https://voyagevax.janeapp.com/locations/voyagevax-langley/book?_gl=1*o1eo9*_gcl_au*MjA2NTE1MjY3MC4xNzY1MjU5MDky*_ga*MTU2MjM2MTM1NS4xNzY1MjU5MDky*_ga_MDXWTC7FKC*czE3NjUyNTkwOTIkbzEkZzEkdDE3NjUyNTkxNzMkajYwJGwwJGgw#/discipline/1/treatment/4';
    } catch (error) {
      console.error('Database save error:', error);
      toast.error('Failed to save contact information');
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
          >
            <div className="bg-white dark:bg-navy-900 rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-8 relative border border-gray-200 dark:border-white/10 my-4 max-h-[95vh] overflow-y-auto">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-white/5"
              >
                <X size={20} />
              </button>

              {/* Header */}
              <div className="mb-5 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Book Virtual Consult
                </h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  You will be redirected to VoyageVax calendar, please book with <span className="font-semibold text-cyan-600 dark:text-cyan-400">Dr. Saba Baig, PharmD</span>
                </p>
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  Enter your details to proceed with booking
                </p>
                <p className="mt-2 text-sm font-semibold text-cyan-600 dark:text-cyan-400">
                  Ages 4+ only
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all text-gray-900 dark:text-white placeholder-gray-400"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all text-gray-900 dark:text-white placeholder-gray-400"
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Referred by Travel Agent or Partner?
                  </label>
                  <div className="flex gap-4 mb-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="isReferred"
                        value="yes"
                        checked={isReferred === 'yes'}
                        onChange={(e) => setIsReferred(e.target.value)}
                        className="w-4 h-4 text-cyan-500 focus:ring-cyan-500 focus:ring-2"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="isReferred"
                        value="no"
                        checked={isReferred === 'no'}
                        onChange={(e) => {
                          setIsReferred(e.target.value);
                          setTravelAgency('');
                        }}
                        className="w-4 h-4 text-cyan-500 focus:ring-cyan-500 focus:ring-2"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                    </label>
                  </div>
                  
                  {isReferred === 'yes' && (
                    <input
                      type="text"
                      id="travelAgency"
                      value={travelAgency}
                      onChange={(e) => setTravelAgency(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all text-gray-900 dark:text-white placeholder-gray-400"
                      placeholder="Enter Travel Agent Referral Code"
                    />
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    rows={4}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all text-gray-900 dark:text-white placeholder-gray-400 resize-none"
                    placeholder="Tell us about your travel plans or questions..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 sm:py-3.5 text-sm sm:text-base bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-bold rounded-lg shadow-lg shadow-blue-500/25 transition-all hover:scale-[1.02] active:scale-95 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {submitting ? 'Processing...' : 'Book Virtual Consult'}
                </button>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
